import { Helmet } from "react-helmet-async";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";

/**
 * FAQ-sektion med accordion + JSON-LD FAQPage-schema.
 * Endast frågor som faktiskt visas på sidan inkluderas i schemat.
 *
 * Props:
 * - eyebrow: liten label ovanför rubriken ("Vanliga frågor")
 * - title: huvudrubrik
 * - intro: valfri ingresstext
 * - items: array av { question, answer }
 * - id: unik sektion-id (för anchor + testid)
 * - includeSchema: true för att rendera JSON-LD (default true)
 */
export default function FAQ({
  eyebrow = "Vanliga frågor",
  title,
  intro,
  items = [],
  id = "faq",
  includeSchema = true,
}) {
  if (!items.length) return null;

  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };

  return (
    <section
      id={id}
      data-testid={`${id}-section`}
      className="bg-[#F7F5F0] py-20 md:py-24"
    >
      {includeSchema && (
        <Helmet>
          <script type="application/ld+json">{JSON.stringify(schema)}</script>
        </Helmet>
      )}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10 md:mb-12">
          <span className="eyebrow">{eyebrow}</span>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1F3A2E] leading-tight tracking-tight">
            {title}
          </h2>
          {intro && (
            <p className="mt-4 text-[#3F4A44] text-base md:text-lg leading-relaxed max-w-2xl">
              {intro}
            </p>
          )}
        </div>

        <Accordion
          type="single"
          collapsible
          className="space-y-3"
          data-testid={`${id}-accordion`}
        >
          {items.map((item, idx) => (
            <AccordionItem
              key={idx}
              value={`item-${idx}`}
              data-testid={`${id}-item-${idx}`}
              className="bg-white border border-[#E5E1D8] rounded-2xl px-5 md:px-6 shadow-sm hover:shadow transition-shadow"
            >
              <AccordionTrigger
                data-testid={`${id}-trigger-${idx}`}
                className="py-5 text-left font-heading font-semibold text-base md:text-lg text-[#1F3A2E] hover:no-underline [&>svg]:text-[#D95D2E] [&>svg]:h-5 [&>svg]:w-5"
              >
                {item.question}
              </AccordionTrigger>
              <AccordionContent
                forceMount
                data-testid={`${id}-content-${idx}`}
                className="pb-5 text-[#3F4A44] text-[15px] md:text-base leading-relaxed"
              >
                {item.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
